import { useCallback, useState } from 'react';
import { API_BASE } from '../../../config/constants';

export function useMessages() {
    const [messages, setMessages] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const loadAllMessages = useCallback(async (): Promise<any[]> => {
        setLoading(true);
        setError(null);
        try { console.debug('[useMessages] loadAllMessages called'); } catch (e) {}
        try {
            const response = await fetch(`${API_BASE}/api/users/get-all-messages`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
            });

            const result = await response.json().catch(() => ({}));

            if (!response.ok || !result?.success) {
                throw new Error(result?.message || 'Failed to fetch messages');
            }

            const rows = Array.isArray(result?.data) ? result.data : [];
            setMessages(rows);
            return rows;
        } catch (err: any) {
            console.error('useMessages: loadAllMessages error', err);
            setError(err?.message || 'Failed to load messages');
            setMessages([]);
            return [];
        } finally {
            setLoading(false);
        }
    }, []);

    const loadMessagesByUser = useCallback((userId: string | number) => {
        // Prefer server-side filtering to ensure up-to-date results
        if (!userId) return [];

        return (async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await fetch(`${API_BASE}/api/users/get-all-messages`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ user_id: userId, limit: 500 }),
                });

                const result = await response.json().catch(() => ({}));
                if (!response.ok || !result?.success) {
                    throw new Error(result?.message || 'Failed to fetch messages for user');
                }

                const rows = Array.isArray(result?.data) ? result.data : [];
                // Optionally update local cache
                setMessages(rows);
                return rows;
            } catch (err: any) {
                console.error('useMessages: loadMessagesByUser error', err);
                setError(err?.message || 'Failed to load messages by user');
                return [];
            } finally {
                setLoading(false);
            }
        })();
    }, [messages]);

    const loadMessagesByOwner = useCallback(async (ownerId: string | number) => {
        if (!ownerId) return [];

        setLoading(true);
        setError(null);
        try {
            // Reuse loadAllMessages to fetch the canonical dataset, then filter by owner_id
            const allRows = await loadAllMessages();
            const rows = Array.isArray(allRows) ? allRows : [];

            const filtered = rows.filter(r => {
                const val = r?.owner_id;
                return String(val) === String(ownerId);
            });

            setMessages(filtered);
            return filtered;
        } catch (err: any) {
            console.error('useMessages: loadMessagesByOwner error', err);
            setError(err?.message || 'Failed to load messages by owner');
            return [];
        } finally {
            setLoading(false);
        }
    }, [loadAllMessages]);

    const createUserObjectMessage = useCallback(async (
        user_id: string | number | null,
        object_id: string | number | null,
        subject: string,
        body: string,
        reply_id?: string | number | null
    ) : Promise<{ success: boolean; message?: string; data?: any } > => {
        setLoading(true);
        setError(null);

        try {
            const url = `${API_BASE}/api/users/create-user-object-message`;
            // Ensure reply_id is explicit null when not provided so the server receives SQL NULL
            const payload = { user_id, object_id, subject, body, reply_id: typeof reply_id === 'undefined' ? null : reply_id };
            try { console.debug('[useMessages] POST', url, payload); } catch (e) {}

            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            const text = await response.text().catch(() => '');
            let result: any = {};
            try {
                result = text ? JSON.parse(text) : {};
            } catch (e) {
                // not JSON
            }

            if (!response.ok || !result?.success) {
                const msg = result?.message || `Request failed: ${response.status} ${response.statusText}` + (text ? ` - ${text.slice(0, 200)}` : '');
                throw new Error(msg);
            }

            return { success: true, message: result.message, data: result.data };
        } catch (err: any) {
            console.error('useMessages: createUserObjectMessage error', err);
            setError(err?.message || 'Failed to create message');
            return { success: false, message: err?.message };
        } finally {
            setLoading(false);
        }
    }, []);

    return {
        messages,
        loading,
        error,
        loadAllMessages,
        loadMessagesByUser,
        loadMessagesByOwner,
        createUserObjectMessage,
    };
}

export default useMessages;
